'use strict';
// public/v3/src/lib/lab-choice-meta.cjs — how a lab CHOICE describes its row (O24).
//
// Three surfaces offer a provider the same compendium rows: the lab card's
// alternatives dropdown, the webapp picker chips, and the SMS numbered list.
// They must not describe a row three different ways, so the strings are
// composed HERE and required by all of them — the server (elation.js,
// smsEngine.js) and the card's pure half (components/order-doc/
// test-alternatives.cjs). Same precedent as the other server↔web shared
// helpers (server/routes/assessmentKiosk.js, bulkPatients.js, elationSync.js).
//
// The input is one row atom as the matcher hands it out:
//   { name, code, cpts?: string[], createdYear?: number, questionCount?: number }
// The three enrichment keys are ABSENT when the mirror does not know them (a
// row not crawled since migration 072, an order-set or practice-created row, a
// session stored before the deploy). Absent means the segment is omitted —
// never guessed at, and never rendered as an empty claim.
//
// `cpts: []` is a DIFFERENT statement from an absent `cpts`: the vendor
// genuinely publishes no CPT for the row, which is worth saying out loud
// ("no CPT on file"), because a row with no billable code is a row a provider
// may want to think twice about.

// A CPT code is 5 characters: four digits and a final digit or letter. That
// covers Category I (82306), II (0008F), III (0042T), PLA (0001U) and MAAA
// (0008M). Measured against 3,537 live sandbox rows, ONE row carries an entry
// that is not CPT-shaped — the Sonic-generation `VITAMIN D, 25 OH` (9179),
// whose "cpts" is its own four-digit order code. Rendering that as a CPT would
// put a number a biller cannot use beside a clinical choice, so only
// CPT-shaped strings render as CPTs; the raw strings stay in the mirror for the
// audit query migration 072's header records.
const CPT_SHAPE = /^\d{4}[\dA-Z]$/;

// How many CPTs are NAMED before the rest become a count. Two, not all: a
// 26-CPT mega-panel is identified by its name, the vendor code already tells
// same-name rows apart, and this segment's job is billing context and
// generation honesty — not a claim list. Measured spread: 1 CPT on 2,647 rows,
// 2 on 376, 3 on 122, 4+ on ~230, max 26.
const CPT_SHOWN = 2;

// The card's own separator. Defined once, here, so it cannot leak into an SMS:
// gsmSafe (server/services/pinpoint.js) transliterates dashes, curly quotes,
// the ellipsis and the bullet, but NOT U+00B7 — one of these in a text message
// forces the whole thing to UCS-2, which the toll-free number was silently
// dropping. `choiceLabel` therefore uses parentheses and commas, and this
// string is reachable only through choiceMetaLine.
const META_SEPARATOR = ' · ';

// WHICH facts an enriched row carries, and what a usable value looks like for
// each. This lives with the RENDERER because the predicate is a rendering
// question — a `cpts` that is not an array, or a year that is not an integer,
// is a fact this module cannot draw, so it is not a fact the row has.
//
// Everything that moves enrichment between objects reads this one list. The
// engine copies what a row HAS (pickEnrichment); the surfaces that re-point a
// line at a different row also DELETE what the new row LACKS, and those two
// falling out of step is how a renamed line ends up showing the previous row's
// CPT. A fourth fact added to the mirror is added here, once.
const ENRICHMENT_FIELDS = Object.freeze({
  cpts: (v) => Array.isArray(v),
  createdYear: (v) => Number.isInteger(v),
  questionCount: (v) => Number.isInteger(v),
});
const ENRICHMENT_KEYS = Object.freeze(Object.keys(ENRICHMENT_FIELDS));

// The enrichment a row actually carries — only the keys with a usable value, so
// a row the mirror has no answer for spreads to nothing. Absent, never null:
// a `cpts: null` on a row would render "no CPT on file", a claim about the
// vendor nothing measured.
function pickEnrichment(row) {
  const out = {};
  if (!row) return out;
  for (const key of ENRICHMENT_KEYS) {
    if (ENRICHMENT_FIELDS[key](row[key])) out[key] = row[key];
  }
  return out;
}

function _cptSegment(cpts) {
  if (!Array.isArray(cpts)) return null;                 // unknown → say nothing
  const codes = cpts.map((c) => String(c == null ? '' : c).trim()).filter((c) => CPT_SHAPE.test(c));
  if (!codes.length) return 'no CPT on file';            // known-empty → say so
  const shown = codes.slice(0, CPT_SHOWN).join(', ');
  const rest = codes.length - CPT_SHOWN;
  return rest > 0 ? `CPT ${shown} +${rest}` : `CPT ${shown}`;
}

// "added 2017". The bare year would be a third unlabelled number beside a
// 5-digit CPT and a 6-digit vendor code; `added` is the shortest word that says
// what it is (owner ruling O29).
function _yearSegment(createdYear) {
  const year = Number(createdYear);
  return Number.isInteger(year) && year > 1900 ? `added ${year}` : null;
}

// Ask-on-entry questions the provider will be asked at requisition time —
// omitted at 0, because "asks 0 questions" is noise on the 3,055 rows that ask
// none. Measured: 482 of 3,537 rows ask at least one, max 26.
function _questionSegment(questionCount) {
  const n = Number(questionCount);
  if (!Number.isInteger(n) || n <= 0) return null;
  return `asks ${n} question${n === 1 ? '' : 's'}`;
}

// The row's enrichment as ordered segments: CPT first (most discriminating and
// the billing fact), then the generation year, then the questions (rarest).
// Empty array when the row carries no enrichment at all.
function choiceSegments(row) {
  const r = row || {};
  return [_cptSegment(r.cpts), _yearSegment(r.createdYear), _questionSegment(r.questionCount)]
    .filter(Boolean);
}

// The card's second line under an option: "CPT 82306 · added 2017". '' when
// there is nothing to say, which is how the .jsx knows not to render the line.
// WEB ONLY — see META_SEPARATOR.
function choiceMetaLine(row) {
  return choiceSegments(row).join(META_SEPARATOR);
}

// One choice as a single line of text, for the surfaces that have only a label:
// the webapp picker chips and the SMS numbered list.
//
//   Vitamin D, 25-Hydroxy (081950, CPT 82306, added 2017)
//   VITAMIN D, 25 OH (4958, no CPT on file, added 2026)
//   HDL Cholesterol (001925)          — enrichment unknown
//   HDL Cholesterol                   — no code either
//
// TRANSPORT, exactly: what this function APPENDS is GSM-7-basic by
// construction — letters, digits, space, and `( ) , + .` — so nothing this
// module adds can be what forces a text message to UCS-2. `name` and `code`
// pass through VERBATIM, because they are the vendor's own values and a choice
// that renamed the assay would be worse than a long message. A non-GSM
// character in either is therefore the TRANSPORT sanitizer's job — `gsmSafe`
// at `sendSms` (server/services/pinpoint.js), the single choke point every SMS
// passes through — not this module's, and no caller may treat `choiceLabel`
// as a sanitizer. (Measured on prod's four compendiums, 15,107 rows: three
// names carry a non-ASCII character, all in compendium 830097159881147 —
// `®`, `™` and a curly apostrophe; `gsmSafe` transliterates the apostrophe and
// today passes the other two through.)
//
// Note that the constraint is the GSM-7 TABLE, not ASCII: a backtick is ASCII
// yet NOT GSM-7, while `é`, `Δ` and `[ ]` are GSM-7. The one character this
// module must never emit on the SMS path is the card's ` · ` (U+00B7), which
// `gsmSafe` does not transliterate — hence META_SEPARATOR is reachable only
// through `choiceMetaLine`, and this function joins with commas.
function choiceLabel(row) {
  const r = row || {};
  const name = String(r.name == null ? '' : r.name).trim();
  const code = r.code == null ? '' : String(r.code).trim();
  const parts = [code, ...choiceSegments(r)].filter(Boolean);
  if (!parts.length) return name;
  return name ? `${name} (${parts.join(', ')})` : `(${parts.join(', ')})`;
}

module.exports = {
  CPT_SHAPE,
  ENRICHMENT_KEYS,
  pickEnrichment,
  choiceSegments,
  choiceMetaLine,
  choiceLabel,
};
